import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@devhop/ui";
import { useRef, useState } from "react";
import type { FeedSessionProductRow } from "@/modules/cf-feed/presentation/api/types";
import {
  type ProductAllocationDraft,
  type ProductAllocationInput,
  parseProductAllocation,
  productAllocationError,
} from "@/modules/cf-feed/presentation/lib/product-allocation";
import { getManualOrderErrorReason } from "@/shared/lib/cf-api-error-messages";
import { ProductAllocationFields } from "./ProductAllocationFields";

export function SessionProductAllocationDialog({
  product,
  isSaving,
  onSave,
  onClose,
}: {
  product: FeedSessionProductRow;
  isSaving: boolean;
  onSave: (id: string, input: ProductAllocationInput) => Promise<unknown>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ProductAllocationDraft>({
    allocationMode: product.isPreorder ? product.allocationMode : "limit",
    allocatedQuantity: product.allocatedQuantity ?? undefined,
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);
  const disabled = pending || isSaving;
  const invalid = productAllocationError(draft);
  const dirty =
    draft.allocationMode !==
    (product.isPreorder ? product.allocationMode : "limit");
  const save = async () => {
    if (savingRef.current || disabled || invalid || !dirty) return;
    savingRef.current = true;
    setPending(true);
    setError(null);
    try {
      await onSave(
        product.id,
        parseProductAllocation({
          allocationMode: product.isPreorder ? draft.allocationMode : "limit",
          allocatedQuantity: draft.allocatedQuantity,
        }),
      );
      onClose();
    } catch (failure) {
      setError(getManualOrderErrorReason(failure));
    } finally {
      savingRef.current = false;
      setPending(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !disabled) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>ແກ້ໄຂໂໝດ CF</DialogTitle>
            <DialogDescription className="break-words">
              {product.title} · {product.variantLabel || product.cfCode}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm">
            ຮັບ CF ແລ້ວ {product.soldQuantity} ໂຕ · ປ່ຽນໂໝດສຳລັບຈຳນວນທີ່ຮັບໃໝ່ເທົ່ານັ້ນ
          </p>
          <ProductAllocationFields
            value={draft}
            onChange={(value) => {
              setDraft(value);
              setError(null);
            }}
            disabled={disabled}
            isPreorder={product.isPreorder}
          />
          {error ? (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={onClose}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={disabled || Boolean(invalid) || !dirty}
            >
              {pending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກໂໝດ CF"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
